import {
  Box,
  formatText,
  replaceQuestionPlaceholders,
  RoundCheckedIcon,
  theme,
  useToast,
  AIIllustration,
} from '@arnold/common';
import { formatStringForEmbeddings, MIN_AI_COMMENT_LENGTH } from '@arnold/core';
import { DotLottiePlayer } from '@dotlottie/react-player';
import styled from '@emotion/styled/macro';
import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  FeEventType,
  ReportSubscriptionSubscription,
  useGetProcessKeywordsSubscriptionSubscription,
  useLogFeEventMutation,
} from '../../../generated/hooks';
import { IFilterDescription } from '../../../lib/reportHelper';
import { usePushReportEvent } from '../../ReportGTMContext';
import i18n from '../../../translations/i18n';
import { AiAnimationContainer } from '../../aiSummary/AiSummaryLoading';
import FilterDescription, { FilterTitleType } from '../FilterDescription';
import { IntercomEvent, IntercomEventSender, sendEventToIntercom } from '../ObserverEventSender';
import RateAi from '../RateAi';
import { useIsMobileView } from '../ResponsiveSwitcher';
import { CommentContent, CommentContentProps } from './CommentContent';
import { ExpandLink } from './ExpandLink';
import { KeyWord } from './KeyWord';

const COMMENT_LIMIT = 9;
const COMMENT_LIMIT_MOBILE = 4;
type CommentBoxProps = {
  header: string;
  subHeader?: string | undefined;
  organizationName?: string;
  filterDescriptions?: IFilterDescription[];
  comments: CommentContentProps[];
  questionLabels?: NonNullable<ReportSubscriptionSubscription['getReport']['questionLabels']>[0];
  emptyState: string;
  loading?: boolean;
  keywordsEnabled: boolean;
  questionIndex: number;
  reportAccess: string;
  reportId: string;
  respondentId?: string;
  isProcessSurvey: boolean;
};

const HeaderBox = styled(Box)`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.c};
`;

const KeyWordsBox = styled(Box)`
  padding-top: ${theme.spacing.f};
  padding-bottom: ${theme.spacing.f};
  margin-top: ${theme.spacing.c};
  margin-bottom: ${theme.spacing.c};
  border: ${theme.spacing.a} solid ${theme.colors.ai.default};

  @container (max-width: 500px) {
    padding-left: ${theme.spacing.f};
    padding-right: ${theme.spacing.f};
  }
  @media (max-width: ${theme.breakpoint.small}) {
    padding-left: ${theme.spacing.f};
    padding-right: ${theme.spacing.f};
  }
`;

const CommentsWrapper = styled.div`
  margin-top: ${theme.spacing.c};
`;

const KeyWordsHeaderWrap = styled.div`
  display: flex;
  justify-content: space-between;
  & > div {
    @container (max-width: 500px) {
      align-self: flex-end;
    }
    @media (max-width: ${theme.breakpoint.small}) {
      align-self: flex-end;
    }
  }
`;

const SeparatorLine = styled.div`
  border-top: 1px solid ${theme.colors.borderSeparator.default};
  margin: ${theme.spacing.f} 0;
`;

const KeyWordsHeader = styled.span`
  font-size: ${theme.typography.body.small?.regular!.fontSize};
  font-weight: ${theme.typography.body.small?.regular!.fontWeight};
  line-height: ${theme.typography.body.small?.regular!.lineHeight};
  color: ${theme.colors.text.secondary};
  margin-left: 36px;
  margin-right: 180px;
  @container (max-width: 500px) {
    &:first-child {
      margin-right: calc(100% - 210px);
    }
    &:last-child {
      max-width: 120px;
      display: inline-block;
      white-space: normal;
    }
  }
  @media (max-width: ${theme.breakpoint.small}) {
    &:first-child {
      margin-right: calc(100% - 210px);
    }
    &:last-child {
      max-width: 120px;
      display: inline-block;
      white-space: normal;
    }
  }
`;

const KeyWordsHeaderContainer = styled.div`
  margin-bottom: ${theme.spacing.f};
  white-space: nowrap;
`;

const KeyWordDescription = styled.div<{ narrow: boolean }>`
  font-size: ${theme.typography.body.small?.regular!.fontSize};
  font-weight: ${theme.typography.body.small?.regular!.fontWeight};
  line-height: ${theme.typography.body.small?.regular!.lineHeight};
  color: ${theme.colors.text.primary};
  margin-top: -${theme.spacing.f};
  margin-right: ${({ narrow }) => (narrow ? '100px' : theme.spacing.f)};
`;

const KeyWordsHeaderWithLogo = styled.div<{ marginBottom: boolean }>`
  margin-bottom: ${({ marginBottom }) => (marginBottom ? theme.spacing.f : 0)};
  display: flex;
  align-items: center;
  @media (max-width: ${theme.breakpoint.small}) {
    cursor: pointer;
  }
`;

const KeyWordsTitle = styled.div`
  margin-left: ${theme.spacing.f};
  width: 100%;
`;

export const CommentBox: FC<CommentBoxProps> = ({
  header,
  subHeader,
  organizationName,
  filterDescriptions,
  comments,
  questionLabels,
  emptyState,
  loading,
  keywordsEnabled,
  questionIndex,
  reportAccess,
  reportId,
  respondentId,
  isProcessSurvey,
}) => {
  const { t } = useTranslation('Report');
  const { addBeautifulToast } = useToast();
  const ref = useRef<HTMLDivElement>(null);
  const pushReportEvent = usePushReportEvent();
  const isMobileView = useIsMobileView();
  const isMobile = isMobileView || !!(ref.current?.clientWidth && ref.current.clientWidth < 500);
  const commentLimit = isMobile ? COMMENT_LIMIT_MOBILE : COMMENT_LIMIT;
  const [displayedComments, setDisplayedComments] = useState<number>(commentLimit);

  const [labels, setLabels] = useState<{ label: string; commentIds: string[] }[]>([]);
  const [selectedLabels, setSelectedLabels] = useState<string[] | null>(null);
  const [showKeyWords, setShowKeyWords] = useState<boolean>(true);
  const [generateKeywords, setGenerateKeywords] = useState<boolean>(false);
  const [logFeEvent] = useLogFeEventMutation();
  keywordsEnabled = true;

  useEffect(() => {
    if (
      (isMobile && displayedComments === COMMENT_LIMIT) ||
      (!isMobile && displayedComments === COMMENT_LIMIT_MOBILE)
    ) {
      setDisplayedComments(commentLimit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  // subscription returns keywords as undefined if it was never generated
  // or empty array if it was generated but there were no keywords
  const { data: processKeywordsData, loading: processKeywordsLoading } = useGetProcessKeywordsSubscriptionSubscription({
    variables: {
      accessToken: reportAccess.split('#')[0], // remove topicGroupId from reportAccess
      languageCode: i18n.language,
      questionIdx: questionIndex.toString(),
      answersIds: comments.map((comment) => comment.id!),
      generate: generateKeywords,
    },
    skip: !isProcessSurvey || comments.length < COMMENT_LIMIT,
    onComplete: () => {
      if (generateKeywords) {
        setShowKeyWords(true);
      }
    },
  });

  const executeLogEvent = (eventType: FeEventType, detail?: string) => {
    try {
      logFeEvent({
        variables: {
          eventType,
          payload: {
            reportAccess,
            reportAccessId: reportId,
            questionIndex,
            respondentId: respondentId?.toString(),
            detail,
          },
        },
      });
    } catch (err) {
      // Do nothing
    }
  };

  const commentsWithoutLabel = comments.filter(
    (comment) =>
      !labels.some((l) => l.commentIds.includes(comment.id!)) &&
      formatStringForEmbeddings(comment.text).length >= MIN_AI_COMMENT_LENGTH,
  );
  const shortCommentsWithoutLabel = comments.filter(
    (comment) =>
      !labels.some((l) => l.commentIds.includes(comment.id!)) &&
      formatStringForEmbeddings(comment.text).length < MIN_AI_COMMENT_LENGTH,
  );

  const containsLabel = (commentId: string, texts: string[]) =>
    labels.some((c) => texts.includes(c.label) && c.commentIds?.includes(commentId));
  const filteredComments = selectedLabels?.length
    ? [
        comments.filter((comment) => containsLabel(comment.id!, selectedLabels)),
        selectedLabels.includes(t('answersWithoutLabels')) ? commentsWithoutLabel : [],
        selectedLabels.includes(t('shortAnswersWithoutLabel')) ? shortCommentsWithoutLabel : [],
      ].flat()
    : comments;

  const nextCommentsCount = Math.min(commentLimit, filteredComments.length - displayedComments);

  const loadMoreComments = () => {
    pushReportEvent('question-comments-fetchnew', 'load_more');
    setDisplayedComments(displayedComments + nextCommentsCount);
  };

  const hideComments = () => {
    setDisplayedComments(commentLimit);
  };

  const handleToggleKeyWords = () => {
    executeLogEvent(showKeyWords ? FeEventType.HideKeyWords : FeEventType.ShowKeyWords);
    pushReportEvent('question-aikeywords-collapse', showKeyWords ? 'collapse' : 'expand');
    setShowKeyWords(!showKeyWords);
  };

  const handleSelectedLabelChange = (selectedLabel: string) => {
    const selectedLabelsCloned = selectedLabels?.slice() || [];
    const selectedLabelIndex = selectedLabelsCloned.indexOf(selectedLabel);

    if (selectedLabelIndex === -1) {
      selectedLabelsCloned.push(selectedLabel);
    } else {
      selectedLabelsCloned.splice(selectedLabelIndex, 1);
    }
    setSelectedLabels(selectedLabelsCloned);
    pushReportEvent('question-aikeywords-filter', 'filter', { keyword: selectedLabel });
    sendEventToIntercom(IntercomEvent.KeywordsFilter, { reportId });
    logFeEvent({
      variables: {
        eventType: FeEventType.ReportAiKeywordsFilter,
        payload: {
          reportAccess,
          reportAccessId: reportId,
          respondentId: respondentId?.toString(),
          detail: selectedLabel,
        },
      },
    });
  };

  useEffect(() => {
    if (keywordsEnabled && comments.length > 5) {
      let filteredLabels = (
        processKeywordsData?.getProcessKeywords.keywords ||
        questionLabels?.questionLabelComments ||
        []
      )
        .map((label) => ({
          ...label,
          // label should only contain comments that are in the comments list
          commentIds: label.commentIds?.filter((c) => comments.some((comment) => c === comment.id)) || [],
        }))
        .filter((label) => label.label.length && label.commentIds && label.commentIds.length > 1)
        .sort((labelA, labelB) => labelB.commentIds!.length - labelA.commentIds!.length)
        .slice(0, 10);
      if (filteredLabels.length === 1) {
        // if there is only one label, we want to hide the entire keywords section instead of showing only one label
        filteredLabels = [];
      }
      setLabels(filteredLabels);
    } else {
      setLabels([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionLabels, keywordsEnabled, processKeywordsData]);

  useEffect(() => {
    if (labels.length && generateKeywords) {
      addBeautifulToast({
        icon: <RoundCheckedIcon />,
        message: t('keywordsAreReady'),
        buttonText: t('scrollToKeywords'),
        duration: 15000,
        buttonOnClick: () => ref.current?.scrollIntoView({ behavior: 'smooth' }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generateKeywords, labels]);

  // number of comments that the biggest keyword has
  const biggestKeyWordCount = Math.max(
    ...labels.map((l) => l.commentIds.length),
    commentsWithoutLabel.length,
    shortCommentsWithoutLabel.length,
  );

  return (
    <div className={'mt-7'} data-cy="report-comment-box" data-comment-box-question-index={questionIndex} ref={ref}>
      <HeaderBox>
        <div data-text-question-index={questionIndex}>
          <h3 className={'mb-0'} data-icom={'report-question-text'} data-for-query-selector-question-text>
            {formatText(
              replaceQuestionPlaceholders(
                header,
                organizationName || `<${t('orgNameReplacement')}>`,
                `<${t('firstDateReplacement')}>`,
                `<${t('teamLeaderReplacement')}>`,
              ),
            )}
          </h3>
          <p className={'mb-0 font-size-xs'} data-for-query-selector-question-description>
            {subHeader}
            {filterDescriptions && !!filterDescriptions.length ? ` ${t('WIDGET_FILTER_RESPONDED')}` : ''}
            {filterDescriptions && !!filterDescriptions.length ? (
              filterDescriptions.map((fd, idx) => (
                <Fragment key={fd.answers && fd.answers[0] && fd.answers[0].answer}>
                  <FilterDescription
                    answers={fd.answers}
                    question={replaceQuestionPlaceholders(
                      fd.question,
                      organizationName || `<${t('orgNameReplacement')}>`,
                      `<${t('firstDateReplacement')}>`,
                      `<${t('teamLeaderReplacement')}>`,
                    )}
                    hideNumbers={loading}
                    filterTitleType={FilterTitleType.REPORT_WIDGET}
                  />
                  {idx + 1 < filterDescriptions.length ? t('FILTER_SEPARATOR') : ''}
                </Fragment>
              ))
            ) : (
              <></>
            )}
          </p>
        </div>
      </HeaderBox>

      {keywordsEnabled &&
        (!!labels.length ||
          (isProcessSurvey &&
            comments.length > COMMENT_LIMIT &&
            !(
              !generateKeywords &&
              processKeywordsData?.getProcessKeywords.done &&
              processKeywordsData?.getProcessKeywords.keywords?.length === 0
            ))) && (
          <>
            <KeyWordsBox>
              <KeyWordsHeaderWithLogo
                marginBottom={!!labels.length}
                onClick={() => isMobileView && handleToggleKeyWords()}
              >
                <AIIllustration />
                <KeyWordsTitle>
                  <KeyWordsHeaderWrap>
                    <h3>{t('keyWords')}</h3>
                    {!!labels.length && (
                      <ExpandLink
                        expanded={showKeyWords}
                        text={isMobileView ? '' : showKeyWords ? t('hideKeyWords') : t('revealKeyWords')}
                        dataIcom={showKeyWords ? t('btn-hide-keywords') : t('btn-reveal-keywords')}
                        click={handleToggleKeyWords}
                      />
                    )}
                    {!labels.length && !generateKeywords && (
                      <GenerateKeywordsButton
                        size="sm"
                        onClick={() => {
                          pushReportEvent('question-aikeywords-generate', 'generate');
                          setGenerateKeywords(true);
                        }}
                      >
                        {t('generateKeywords')}
                      </GenerateKeywordsButton>
                    )}
                  </KeyWordsHeaderWrap>
                  <KeyWordDescription narrow={!labels.length && !generateKeywords}>
                    {`${t('keyWordsDescription')} `}
                    <b data-icom="report-powered-by-ai">{t('ai')}</b>
                  </KeyWordDescription>
                </KeyWordsTitle>
              </KeyWordsHeaderWithLogo>
              {showKeyWords && !!labels.length && (
                <div data-for-query-selector-ai-keywords data-icom="report-key-words-table">
                  <KeyWordsHeaderContainer>
                    <KeyWordsHeader>{t('theme')}</KeyWordsHeader>
                    <KeyWordsHeader>{t('numberOfAnswers')}</KeyWordsHeader>
                  </KeyWordsHeaderContainer>
                  {labels.map((l) => (
                    <KeyWord
                      key={l.label}
                      keyWord={l.label}
                      count={l.commentIds.length}
                      maxCount={comments.length}
                      biggestKeyWordCount={biggestKeyWordCount}
                      selected={!!selectedLabels?.includes(l.label)}
                      onSelect={() => handleSelectedLabelChange(l.label)}
                      t={t}
                    />
                  ))}
                  {(commentsWithoutLabel.length > 0 || shortCommentsWithoutLabel.length > 0) && <SeparatorLine />}
                  {commentsWithoutLabel.length > 0 && (
                    <KeyWord
                      key={'answersWithoutLabels'}
                      keyWord={t('answersWithoutLabels')}
                      count={commentsWithoutLabel.length}
                      maxCount={comments.length}
                      biggestKeyWordCount={biggestKeyWordCount}
                      selected={!!selectedLabels?.includes(t('answersWithoutLabels'))}
                      onSelect={() => handleSelectedLabelChange(t('answersWithoutLabels'))}
                      t={t}
                    />
                  )}
                  {shortCommentsWithoutLabel.length > 0 && (
                    <KeyWord
                      key={'shortAnswersWithoutLabel'}
                      keyWord={t('shortAnswersWithoutLabel')}
                      count={shortCommentsWithoutLabel.length}
                      maxCount={comments.length}
                      biggestKeyWordCount={biggestKeyWordCount}
                      selected={!!selectedLabels?.includes(t('shortAnswersWithoutLabel'))}
                      onSelect={() => handleSelectedLabelChange(t('shortAnswersWithoutLabel'))}
                      t={t}
                    />
                  )}
                </div>
              )}
              {showKeyWords &&
                !labels.length &&
                generateKeywords &&
                !processKeywordsLoading &&
                processKeywordsData?.getProcessKeywords.done && (
                  <NoKeywordsGeneratedContainer>{t('noKeywordsWereGenerated')}</NoKeywordsGeneratedContainer>
                )}
              {!labels.length && (processKeywordsLoading || !processKeywordsData?.getProcessKeywords.done) && (
                <>
                  <AiAnimationContainer>
                    <DotLottiePlayer src={`/animations/aiLoading.lottie`} autoplay loop></DotLottiePlayer>
                  </AiAnimationContainer>
                  <p style={{ textAlign: 'center' }}>
                    {t('SurveySummary:loadingDescription1')}
                    <br />
                    {t('SurveySummary:loadingDescription2')}
                  </p>
                </>
              )}
            </KeyWordsBox>

            <RateAi type="KEYWORDS" />
            <IntercomEventSender eventName={IntercomEvent.KeywordsSeen} data={{ reportId }} />
          </>
        )}
      {filteredComments.length > 0 ? (
        <>
          <CommentsWrapper className={'row mx-n2'} data-for-query-selector-comments>
            {filteredComments
              .filter((_, index) => index < displayedComments)
              .map(({ author, teamName, text }, index) => (
                <CommentContent key={index} author={author} teamName={teamName} text={text} />
              ))}
          </CommentsWrapper>
          {filteredComments.length >= commentLimit && (
            <ButtonsWrapper className={'mt-5 pl-8'}>
              <StyledButton
                onClick={loadMoreComments}
                disabled={filteredComments.length <= displayedComments}
                data-cy="report-comments-load-more"
                className={'btn-fill'}
              >
                {t('MORE_COMMENTS', {
                  count: filteredComments.length - displayedComments,
                })}
              </StyledButton>
              {displayedComments > commentLimit && (
                <StyledButton
                  onClick={hideComments}
                  data-cy="report-comments-hide"
                  className={'ml-6 btn-empty'}
                  data-pptx-click
                >
                  {t('HIDE_COMMENTS')}
                </StyledButton>
              )}
            </ButtonsWrapper>
          )}
        </>
      ) : loading ? (
        <div className={'row mx-n2'}>
          <CommentContent key={0} text="" loading={true} />
          <CommentContent key={1} text="" loading={true} />
          <CommentContent key={2} text="" loading={true} />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm p-8 no-data">
          <span>{emptyState}</span>
        </div>
      )}
    </div>
  );
};

const GenerateKeywordsButton = styled(Button)`
  &,
  :hover,
  :focus,
  :active {
    padding: 0px ${theme.spacing.h};
    background-color: ${theme.colors.ai.default} !important;
    border-color: ${theme.colors.ai.default} !important;
    box-shadow: none !important;
  }
  :hover {
    filter: saturate(0.6);
  }
  @container (max-width: 500px) {
    padding: 0px ${theme.spacing.f};
    height: ${theme.spacing.h};
  }
  @media (max-width: ${theme.breakpoint.small}) {
    padding: 0px ${theme.spacing.f};
    height: ${theme.spacing.h};
  }
`;
const NoKeywordsGeneratedContainer = styled.div`
  padding: ${theme.spacing.f};
  padding-top: ${theme.spacing.h};
  text-align: center;
`;

const StyledButton = styled(Button)`
  @container (max-width: 500px) {
    width: 100%;
    margin-left: 0 !important;
    margin-bottom: ${theme.spacing.f} !important;
  }
  @media (max-width: ${theme.breakpoint.small}) {
    width: 100%;
    margin-left: 0 !important;
    margin-bottom: ${theme.spacing.f} !important;
  }
`;

const ButtonsWrapper = styled.div`
  @container (max-width: 500px) {
    margin-left: ${theme.spacing.h} !important;
    margin-right: ${theme.spacing.h} !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
  @media (max-width: ${theme.breakpoint.small}) {
    margin-left: ${theme.spacing.h} !important;
    margin-right: ${theme.spacing.h} !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
`;
